import { useEffect, useState } from "react";
import { get, post, ago, type RespostaRuim } from "../lib/api";
import { Card, Empty, Carregando, Segmentos, Button, Abas } from "../lib/ui";
import {
  AbaMensagens,
  AbaFollowup,
  AbaCorretor,
  IaTopo,
  type AnaliseDetalhada,
} from "./AnaliseDetalhe";
import {
  LayoutDashboard,
  MessagesSquare,
  BellRing,
  Headset,
  Sparkles,
  CalendarCheck,
  UserRound,
  MessageSquare,
  Timer,
  Wallet,
  Repeat,
  Check,
  CheckCheck,
  type LucideIcon,
} from "lucide-react";

/**
 * O que o atendimento entregou, e quanto custou.
 *
 * O painel media atendimento — leads, tempo de resposta, quem esperou humano —
 * e nada media o resultado. Mas o objetivo do sistema é marcar conversa, não
 * responder rápido: responder em oito segundos para ninguém agendar é um bom
 * número escondendo um mau negócio.
 *
 * A tabela por campanha é a peça que mais muda decisão. Ela responde qual
 * anúncio traz lead que MARCA, não lead que clica — e é com esse número que se
 * decide onde colocar verba na semana seguinte.
 */

interface Funil {
  leads: number;
  abordados: number;
  responderam: number;
  agendaram: number;
  compareceram: number;
  nao_compareceram: number;
  agendados_futuros: number;
}

interface PorCampanha {
  campanha: string;
  leads: number;
  responderam: number;
  agendaram: number;
  custo_usd: number;
}

interface Esforco {
  mensagens_por_agendamento: number | null;
  minutos_ate_agendar: number | null;
  custo_por_agendamento_usd: number | null;
  custo_por_lead_usd: number | null;
  custo_conversa_agendada_usd: number | null;
}

/** 38 min, 2 h, 3 dias -- a unidade que cabe no numero. */
function duracao(min: number | null): string {
  if (min === null) return "—";
  if (min < 60) return `${min} min`;
  const h = Math.round(min / 60);
  if (h < 48) return `${h} h`;
  return `${Math.round(h / 24)} dias`;
}

interface Tokens {
  tokens_in: number;
  tokens_cached: number;
  tokens_cache_write: number;
  tokens_out: number;
  cache_hit_pct: number | null;
  custo_write_pct: number | null;
  custo_saida_pct: number | null;
  turnos_frios: number;
  usd_frios: number;
  turnos: number;
  usd: number;
  aquecimento_usd: number;
  aquecimentos: number;
  aquecimentos_frios: number;
  simulador_usd: number;
  usd_leitura: number;
  usd_gravacao: number;
  usd_entrada: number;
  usd_saida: number;
  usd_followup: number;
  conversas: number;
  chamadas_media: number | null;
}

interface PorModelo {
  modelo: string;
  respostas: number;
  usd: number;
  usd_por_resposta: number;
  chamadas_por_resposta: number | null;
  saida_por_resposta: number;
}

interface Dados {
  dias: number;
  funil: Funil;
  campanhas: PorCampanha[];
  esforco: Esforco;
  perdas: { motivo: string; quantos: number }[];
  tokens: Tokens;
  porDia: GastoDia[];
  porModelo: PorModelo[];
  restanteUsd: number | null;
}

interface GastoDia {
  dia: string;
  conversa: number;
  aquecimento: number;
  simulador: number;
}

const JANELAS = [7, 30, 90] as const;

const pct = (parte: number, todo: number) =>
  todo > 0 ? `${Math.round((100 * parte) / todo)}%` : "—";

const usd = (n: number | null) => (n === null ? "—" : `US$ ${n.toFixed(2)}`);

type Aba = "geral" | "mensagens" | "followup" | "corretor" | "ia";

const ABAS: { id: Aba; rotulo: string; icone: typeof LayoutDashboard }[] = [
  { id: "geral", rotulo: "Visão geral", icone: LayoutDashboard },
  { id: "mensagens", rotulo: "Mensagens", icone: MessagesSquare },
  { id: "followup", rotulo: "Follow-up", icone: BellRing },
  { id: "corretor", rotulo: "Corretor", icone: Headset },
  { id: "ia", rotulo: "IA e custo", icone: Sparkles },
];

export function Analise() {
  const [dias, setDias] = useState<number>(30);
  const [aba, setAba] = useState<Aba>(() => {
    try {
      const s = localStorage.getItem("analise.aba");
      return ABAS.some((a) => a.id === s) ? (s as Aba) : "geral";
    } catch {
      return "geral";
    }
  });
  const [d, setD] = useState<Dados | null>(null);
  const [det, setDet] = useState<AnaliseDetalhada | null>(null);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const trocarAba = (a: Aba) => {
    setAba(a);
    try {
      localStorage.setItem("analise.aba", a);
    } catch {
      /* sem armazenamento: só não lembra a aba */
    }
  };

  // Ao trocar o período, os números antigos ficam na tela esmaecidos até os
  // novos chegarem: sumir com tudo e mostrar esqueleto faz a página pular.
  useEffect(() => {
    setCarregando(true);
    get<Dados>(`/analise?dias=${dias}`)
      .then((r) => {
        setD(r);
        setErro(null);
      })
      .catch((e: Error) => setErro(e.message))
      .finally(() => setCarregando(false));
  }, [dias]);

  // O detalhe é mais pesado e só é pedido quando alguém sai da visão geral.
  const precisaDetalhe = aba !== "geral";
  useEffect(() => {
    if (!precisaDetalhe) return;
    if (det && det.dias === dias) return;
    setCarregando(true);
    get<AnaliseDetalhada>(`/analise/detalhe?dias=${dias}`)
      .then((r) => {
        setDet(r);
        setErro(null);
      })
      .catch((e: Error) => setErro(e.message))
      .finally(() => setCarregando(false));
  }, [dias, precisaDetalhe]); // eslint-disable-line react-hooks/exhaustive-deps

  if (erro) return <Empty>{erro}</Empty>;
  if (!d) return <Carregando />;

  const f = d.funil;
  const realizados = f.compareceram + f.nao_compareceram;

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3 flex-wrap">
        <span className="text-sm text-ink-3">Período</span>
        <Segmentos
          atual={dias}
          onChange={setDias}
          opcoes={JANELAS.map((n) => ({ id: n as number, rotulo: `${n} dias` }))}
        />
      </div>

      <Abas abas={ABAS} atual={aba} onChange={trocarAba} />

      <div className={`space-y-6 transition-opacity ${carregando ? "opacity-50" : ""}`}>
      {precisaDetalhe && !det && <Carregando />}
      {aba === "mensagens" && det && <AbaMensagens d={det} />}
      {aba === "followup" && det && <AbaFollowup d={det} />}
      {aba === "corretor" && det && <AbaCorretor d={det} />}
      {aba === "ia" && (
        <>
          {det && <IaTopo d={det} />}
          <GastoDiario dias={d.porDia} restante={d.restanteUsd} />
          <OndeVaiODinheiro t={d.tokens} modelos={d.porModelo ?? []} />
        </>
      )}

      {aba === "geral" && (
      <>

      {/* ------------------------------------------------------------------ */}
      {/* O funil, em etapas que se perdem uma na outra                       */}
      {/* ------------------------------------------------------------------ */}
      <section>
        <h2 className="text-sm font-semibold text-ink mb-3">Do lead ao horário marcado</h2>
        <Card className="p-5">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <Etapa rotulo="Leads" valor={f.leads} base={f.leads} />
            <Etapa
              rotulo="Responderam"
              valor={f.responderam}
              base={f.leads}
              nota={`${pct(f.responderam, f.leads)} dos leads`}
            />
            <Etapa
              rotulo="Agendaram"
              valor={f.agendaram}
              base={f.leads}
              nota={`${pct(f.agendaram, f.responderam)} de quem respondeu`}
              destaque
            />
            <Etapa
              rotulo="Compareceram"
              valor={f.compareceram}
              base={f.leads}
              nota={
                realizados > 0
                  ? `${pct(f.compareceram, realizados)} dos que aconteceram`
                  : "ninguém marcou como realizado ainda"
              }
            />
          </div>

          <p className="text-xs text-ink-3 mt-4 max-w-prose">
            {f.agendados_futuros > 0 && (
              <>
                {f.agendados_futuros} agendamento
                {f.agendados_futuros > 1 ? "s" : ""} ainda por acontecer.{" "}
              </>
            )}
            {realizados === 0 && f.agendaram > 0 && (
              <>
                A coluna de comparecimento só enche quando alguém marca o
                agendamento como realizado ou não compareceu, na tela Agenda.
                Sem isso não dá para saber se a IA agenda gente séria.
              </>
            )}
          </p>
        </Card>
      </section>

      {/* ------------------------------------------------------------------ */}
      {/* Custo e esforço                                                     */}
      {/* ------------------------------------------------------------------ */}
      <section>
        <h2 className="text-sm font-semibold text-ink mb-3">Custo e esforço</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Numero
            icone={CalendarCheck}
            rotulo="Custo por agendamento"
            valor={usd(d.esforco.custo_por_agendamento_usd)}
            ajuda={
              d.esforco.custo_conversa_agendada_usd !== null
                ? `Todo o gasto do modelo, inclusive com quem não agendou. A conversa que agendou em si custou ${usd(d.esforco.custo_conversa_agendada_usd)}. Mídia fora.`
                : "Todo o gasto do modelo, inclusive com quem não agendou. Mídia fora."
            }
          />
          <Numero
            icone={UserRound}
            rotulo="Custo por lead"
            valor={usd(d.esforco.custo_por_lead_usd)}
            ajuda="Todo lead conta, inclusive quem nunca respondeu."
          />
          <Numero
            icone={MessageSquare}
            rotulo="Mensagens até fechar"
            valor={d.esforco.mensagens_por_agendamento ?? "—"}
            ajuda="Mediana. A conversa típica, não a pior."
          />
          <Numero
            icone={Timer}
            rotulo="Tempo até fechar"
            valor={duracao(d.esforco.minutos_ate_agendar)}
            ajuda="Da primeira mensagem até o horário ser marcado."
          />
        </div>
      </section>

      {/* ------------------------------------------------------------------ */}
      {/* Campanha: a tabela que muda decisão de verba                        */}
      {/* ------------------------------------------------------------------ */}
      <section>
        <h2 className="text-sm font-semibold text-ink mb-3">Por campanha</h2>
        {d.campanhas.length === 0 ? (
          <Empty>Nenhum lead com campanha identificada nesta janela.</Empty>
        ) : (
          <Card className="p-0 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-ink-3 text-xs border-b border-rule bg-surface-2/50">
                  <th className="text-left font-medium px-4 py-3">Campanha</th>
                  <th className="text-right font-medium px-4 py-3">Leads</th>
                  <th className="text-right font-medium px-4 py-3">Responderam</th>
                  <th className="text-right font-medium px-4 py-3">Agendaram</th>
                  <th className="text-right font-medium px-4 py-3">Conversão</th>
                  <th className="text-right font-medium px-4 py-3">Custo IA</th>
                </tr>
              </thead>
              <tbody>
                {d.campanhas.map((c) => (
                  <tr key={c.campanha} className="border-b border-rule last:border-0 hover:bg-surface-2/40 transition-colors">
                    <td className="p-3 text-ink-2 max-w-xs truncate" title={c.campanha}>
                      {c.campanha}
                    </td>
                    <td className="p-3 text-right tabular-nums">{c.leads}</td>
                    <td className="p-3 text-right tabular-nums text-ink-3">
                      {c.responderam}
                    </td>
                    <td className="p-3 text-right tabular-nums text-ink">
                      {c.agendaram}
                    </td>
                    <td className="p-3 text-right tabular-nums">
                      <span className={c.agendaram > 0 ? "text-ok" : "text-ink-3"}>
                        {pct(c.agendaram, c.leads)}
                      </span>
                    </td>
                    <td className="p-3 text-right tabular-nums text-ink-3">
                      {c.custo_usd.toFixed(2)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        )}
        <p className="text-xs text-ink-3 mt-2 max-w-prose">
          Conversão é agendamento sobre lead, e é o número que decide verba:
          campanha que traz muito clique e nenhum horário marcado está comprando
          curiosidade. O crédito vai para a campanha que trouxe o lead, não para
          um resgate feito no painel depois.
        </p>
      </section>

      <RespostasRuins />

      {/* ------------------------------------------------------------------ */}
      {/* Perdas na porta                                                     */}
      {/* ------------------------------------------------------------------ */}
      {d.perdas.length > 0 && (
        <section>
          <h2 className="text-sm font-semibold text-ink mb-3">Leads que pararam na porta</h2>
          <Card className="p-4">
            <div className="space-y-1">
              {d.perdas.map((p) => (
                <div key={p.motivo} className="flex justify-between text-sm">
                  <span className="text-ink-2">{p.motivo}</span>
                  <span className="text-ink tabular-nums">{p.quantos}</span>
                </div>
              ))}
            </div>
            <p className="text-xs text-ink-3 mt-3 max-w-prose">
              Abordagens que nunca saíram. Cada linha é dinheiro de anúncio que
              entrou e não virou conversa — vale olhar antes de aumentar verba.
            </p>
          </Card>
        </section>
      )}
      </>
      )}
      </div>
    </div>
  );
}

function Etapa({
  rotulo,
  valor,
  base,
  nota,
  destaque,
}: {
  rotulo: string;
  valor: number;
  /** Os leads do periodo: a barra mostra quanto sobrou deles nesta etapa. */
  base: number;
  nota?: string;
  destaque?: boolean;
}) {
  const largura = base > 0 ? Math.max(2, (100 * valor) / base) : 0;
  return (
    <div>
      <div className="text-xs font-medium text-ink-3">{rotulo}</div>
      <div
        className={`text-3xl font-semibold tracking-tight tabular-nums mt-1.5 ${
          destaque ? "text-accent" : "text-ink"
        }`}
      >
        {valor}
      </div>
      <div className="h-1.5 rounded-full bg-surface-3 mt-3 overflow-hidden">
        <div
          className={`h-full rounded-full ${destaque ? "bg-accent" : "bg-ink-3/60"}`}
          style={{ width: `${largura}%` }}
        />
      </div>
      {nota && <div className="text-xs text-ink-3 mt-2">{nota}</div>}
    </div>
  );
}

function Numero({
  rotulo,
  valor,
  ajuda,
  icone: Icone,
}: {
  rotulo: string;
  valor: string | number;
  ajuda: string;
  icone?: LucideIcon;
}) {
  return (
    <Card className="p-4">
      <div className="flex items-center gap-2">
        {Icone && (
          <span className="w-6 h-6 rounded-md grid place-items-center bg-accent-dim text-accent shrink-0">
            <Icone size={14} strokeWidth={2.2} />
          </span>
        )}
        <div className="text-xs font-medium text-ink-3">{rotulo}</div>
      </div>
      <div className="text-2xl font-semibold tracking-tight text-ink tabular-nums mt-3">
        {valor}
      </div>
      <div className="text-xs text-ink-3 mt-1.5 leading-snug">{ajuda}</div>
    </Card>
  );
}

/**
 * Gasto dia a dia, e para onde o mês caminha.
 *
 * O total do período não mostra tendência: não dá para ver se um deploy
 * baixou o custo, nem quando o saldo acaba. A projeção usa os últimos sete
 * dias COMPLETOS -- hoje ainda está pela metade e puxaria a média para baixo.
 * Simulador fica fora da projeção: é teste, não operação.
 */
function GastoDiario({
  dias,
  restante,
}: {
  dias: GastoDia[];
  restante: number | null;
}) {
  if (dias.length === 0) return null;

  const total = (g: GastoDia) => g.conversa + g.aquecimento + g.simulador;
  const maior = Math.max(...dias.map(total), 0.01);

  // A partir do primeiro dia com gasto: os dias antes de o sistema existir
  // entrariam como zero e puxariam a projeção para baixo.
  const primeiro = Math.max(0, dias.findIndex((g) => total(g) > 0));
  const completos = dias.slice(primeiro, -1).slice(-7);
  const mediaDia =
    completos.length > 0
      ? completos.reduce((s, g) => s + g.conversa + g.aquecimento, 0) /
        completos.length
      : null;
  const duraDias =
    restante !== null && mediaDia && mediaDia > 0
      ? Math.floor(restante / mediaDia)
      : null;

  const dm = (iso: string) => `${iso.slice(8, 10)}/${iso.slice(5, 7)}`;

  return (
    <section>
      <h2 className="text-sm font-semibold text-ink mb-3">Gasto por dia</h2>
      <Card className="p-5">
        <div className="flex items-end gap-[3px] h-32 border-b border-rule">
          {dias.map((g) => (
            <div
              key={g.dia}
              className="flex-1 flex flex-col-reverse min-w-0 rounded-t-[3px] overflow-hidden hover:brightness-125 transition-[filter]"
              style={{ height: `${(total(g) / maior) * 100}%` }}
              title={
                `${dm(g.dia)}: US$ ${total(g).toFixed(2)}` +
                ` (conversa ${g.conversa.toFixed(2)}, aquecimento ${g.aquecimento.toFixed(2)}` +
                (g.simulador > 0 ? `, simulador ${g.simulador.toFixed(2)}` : "") +
                ")"
              }
            >
              <div
                className="bg-accent"
                style={{ height: `${(g.conversa / Math.max(total(g), 1e-9)) * 100}%` }}
              />
              <div
                className="bg-warm"
                style={{ height: `${(g.aquecimento / Math.max(total(g), 1e-9)) * 100}%` }}
              />
              <div
                className="bg-ink-3"
                style={{ height: `${(g.simulador / Math.max(total(g), 1e-9)) * 100}%` }}
              />
            </div>
          ))}
        </div>
        <div className="flex justify-between text-[11px] text-ink-3 mt-1.5 tabular-nums">
          <span>{dm(dias[0]!.dia)}</span>
          <span>hoje</span>
        </div>

        <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-ink-3 mt-3">
          <span>
            <span className="inline-block w-2 h-2 rounded-full bg-accent mr-1.5" />
            conversas e toques
          </span>
          <span>
            <span className="inline-block w-2 h-2 rounded-full bg-warm mr-1.5" />
            aquecimento do cache
          </span>
          <span>
            <span className="inline-block w-2 h-2 rounded-full bg-ink-3 mr-1.5" />
            simulador
          </span>
        </div>

        {mediaDia !== null && (
          <p className="text-xs text-ink-3 mt-3 max-w-prose">
            Nos últimos {completos.length} dias completos, US${" "}
            {mediaDia.toFixed(2)} por dia — cerca de US$ {(mediaDia * 30).toFixed(0)}{" "}
            em 30 dias nesse ritmo.
            {duraDias !== null &&
              ` O saldo de US$ ${restante!.toFixed(2)} dura uns ${duraDias} dias.`}
          </p>
        )}
      </Card>
    </section>
  );
}

/** Valor pequeno com casas suficientes: US$ 0.012 não pode virar US$ 0.01. */
const usdFino = (n: number) => `US$ ${n < 0.1 ? n.toFixed(3) : n.toFixed(2)}`;

const ROTULO_MODELO: Record<string, string> = {
  "claude-opus-5": "Opus 5",
  "claude-sonnet-5": "Sonnet 5",
};

/**
 * O custo do atendimento, visto de três lados.
 *
 * Por COMPONENTE (o que a API cobra): leitura de cache, gravação, entrada nova
 * e saída têm preços muito diferentes — o mesmo token sai vinte vezes mais
 * caro gravado do que lido. Em dólar, e não só em porcentagem: "gravação 64%"
 * não dizia se eram dois dólares ou duzentos.
 *
 * Por TIPO (o que gerou o gasto): resposta a lead, toque de follow-up,
 * aquecimento do cache e simulador.
 *
 * Por MODELO: para a troca Opus/Sonnet ser decidida com número.
 *
 * E as chamadas por resposta, que é o que mais explica o custo de cada uma:
 * cada ferramenta usada devolve o resultado ao modelo numa chamada nova, que
 * relê o prompt e pensa de novo.
 */
function OndeVaiODinheiro({ t, modelos }: { t: Tokens; modelos: PorModelo[] }) {
  if (t.turnos === 0) return null;

  const mil = (n: number) =>
    n >= 1_000_000 ? `${(n / 1_000_000).toFixed(1)}M` : `${(n / 1000).toFixed(0)}k`;
  const write = t.custo_write_pct ?? 0;
  const historicoUsd = Math.max(0, t.usd_gravacao - t.usd_frios);
  // Uma em cada cinco respostas pagando o prompt inteiro: o aquecimento
  // deveria ter segurado a maior parte disso.
  const frioDemais = t.turnos_frios / t.turnos > 0.2;

  const componentes = [
    {
      rotulo: "Leitura do cache",
      usd: t.usd_leitura,
      cor: "bg-ok",
      diz: `${mil(t.tokens_cached)} tokens a 1/10 do preço`,
    },
    {
      rotulo: "Gravação do cache",
      usd: t.usd_gravacao,
      cor: "bg-warm",
      diz: `${mil(t.tokens_cache_write)} tokens a 2× o preço`,
    },
    {
      rotulo: "Entrada nova",
      usd: t.usd_entrada,
      cor: "bg-[#8aa4c8]",
      diz: `${mil(t.tokens_in)} tokens a preço cheio`,
    },
    {
      rotulo: "Saída",
      usd: t.usd_saida,
      cor: "bg-accent",
      diz: `raciocínio + texto, ~${Math.round(t.tokens_out / t.turnos)} tokens por resposta`,
    },
  ];
  const somaComp = componentes.reduce((a, c) => a + c.usd, 0) || 1;

  const tipos = [
    { rotulo: "Respostas a leads", usd: Math.max(0, t.usd - t.usd_followup) },
    { rotulo: "Toques de follow-up", usd: t.usd_followup },
    { rotulo: "Aquecimento do cache", usd: t.aquecimento_usd },
    { rotulo: "Simulador", usd: t.simulador_usd },
  ];
  const maiorTipo = Math.max(...tipos.map((x) => x.usd), 0.0001);

  return (
    <section className="space-y-3">
      <h2 className="text-sm font-semibold text-ink">Custo do atendimento</h2>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Numero
          icone={Wallet}
          rotulo="Gasto no período"
          valor={usd(t.usd + t.aquecimento_usd)}
          ajuda="Respostas, toques e aquecimento. O simulador fica à parte."
        />
        <Numero
          icone={MessageSquare}
          rotulo="Por resposta"
          valor={usdFino(t.usd / t.turnos)}
          ajuda={`Média de ${t.turnos} respostas da IA no período.`}
        />
        <Numero
          icone={UserRound}
          rotulo="Por conversa"
          valor={t.conversas > 0 ? usdFino(t.usd / t.conversas) : "—"}
          ajuda={`O que um lead custou, somando as respostas. ${t.conversas} conversas.`}
        />
        <Numero
          icone={Repeat}
          rotulo="Chamadas por resposta"
          valor={t.chamadas_media ?? "—"}
          ajuda="1 é resposta direta. Cada rodada de ferramenta soma uma chamada."
        />
      </div>

      <Card className="p-5" stripe={write >= 40 ? "warm" : undefined}>
        <div className="flex items-baseline justify-between gap-3 flex-wrap">
          <h3 className="text-sm font-semibold text-ink">Onde o dinheiro vai</h3>
          <span className="text-xs text-ink-3">
            {t.cache_hit_pct ?? "—"}% da entrada lida do cache
          </span>
        </div>

        {/* A barra: cada fatia do tamanho do que custou. */}
        <div className="flex h-3 rounded-full overflow-hidden bg-surface-3 mt-4">
          {componentes.map((c) => (
            <div
              key={c.rotulo}
              className={c.cor}
              style={{ width: `${(100 * c.usd) / somaComp}%` }}
              title={`${c.rotulo}: US$ ${c.usd.toFixed(2)}`}
            />
          ))}
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
          {componentes.map((c) => (
            <div key={c.rotulo}>
              <div className="flex items-center gap-1.5 text-xs text-ink-3">
                <span className={`w-2 h-2 rounded-full ${c.cor}`} />
                {c.rotulo}
              </div>
              <div className="flex items-baseline gap-2 mt-1">
                <span className="text-xl font-semibold tracking-tight text-ink tabular-nums">
                  {usd(c.usd)}
                </span>
                <span className="text-xs text-ink-3 tabular-nums">
                  {Math.round((100 * c.usd) / somaComp)}%
                </span>
              </div>
              <div className="text-[11px] text-ink-3 mt-0.5">{c.diz}</div>
            </div>
          ))}
        </div>

        <p className="text-xs text-ink-3 mt-5 max-w-prose leading-relaxed">
          {t.aquecimentos > 0 && (
            <>
              Manter o cache quente custou US$ {t.aquecimento_usd.toFixed(2)} (
              {t.aquecimentos} aquecimentos, {t.aquecimentos_frios} acharam o
              cache frio — o esperado é um por dia, mais um por deploy ou troca
              de modelo).{" "}
            </>
          )}
          {t.usd_gravacao > 0 && (
            <>
              Da gravação, US$ {t.usd_frios.toFixed(2)} foram {t.turnos_frios}{" "}
              {t.turnos_frios === 1 ? "resposta" : "respostas"} que acharam o
              prompt frio e regravaram tudo, e US$ {historicoUsd.toFixed(2)}{" "}
              foram o histórico das conversas.{" "}
            </>
          )}
          {frioDemais
            ? "Muitas respostas acharam o prompt frio: ou o aquecimento não cobre esses horários, ou foram deploys que mudaram o prompt."
            : write >= 40
              ? "A gravação vem do histórico: é quem volta depois de mais de uma hora e tem a conversa regravada. É o custo do ritmo do WhatsApp, não defeito."
              : "A gravação de cache está numa fatia saudável: ele está sendo relido mais do que reescrito."}
        </p>
      </Card>

      <div className="grid md:grid-cols-2 gap-3">
        <Card className="p-5">
          <h3 className="text-sm font-semibold text-ink mb-4">Por tipo</h3>
          <div className="space-y-3">
            {tipos.map((x) => (
              <div key={x.rotulo}>
                <div className="flex justify-between text-xs">
                  <span className="text-ink-2">{x.rotulo}</span>
                  <span className="text-ink tabular-nums">{usd(x.usd)}</span>
                </div>
                <div className="h-1.5 rounded-full bg-surface-3 mt-1.5 overflow-hidden">
                  <div
                    className="h-full rounded-full bg-accent/70"
                    style={{ width: `${(100 * x.usd) / maiorTipo}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </Card>

        <Card className="p-5">
          <h3 className="text-sm font-semibold text-ink mb-3">Por modelo</h3>
          {modelos.length === 0 ? (
            <p className="text-xs text-ink-3">Sem respostas no período.</p>
          ) : (
            <div className="overflow-x-auto -mx-1">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs text-ink-3">
                    <th className="text-left font-medium px-1 pb-2">Modelo</th>
                    <th className="text-right font-medium px-1 pb-2">Respostas</th>
                    <th className="text-right font-medium px-1 pb-2">Por resposta</th>
                    <th className="text-right font-medium px-1 pb-2">Chamadas</th>
                    <th className="text-right font-medium px-1 pb-2">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {modelos.map((m) => (
                    <tr key={m.modelo} className="border-t border-rule">
                      <td className="px-1 py-2 text-ink">
                        {ROTULO_MODELO[m.modelo] ?? m.modelo}
                      </td>
                      <td className="px-1 py-2 text-right tabular-nums text-ink-2">
                        {m.respostas}
                      </td>
                      <td className="px-1 py-2 text-right tabular-nums text-ink">
                        {usdFino(m.usd_por_resposta)}
                      </td>
                      <td className="px-1 py-2 text-right tabular-nums text-ink-2">
                        {m.chamadas_por_resposta ?? "—"}
                      </td>
                      <td className="px-1 py-2 text-right tabular-nums text-ink-2">
                        {usd(m.usd)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <p className="text-[11px] text-ink-3 mt-3 leading-relaxed">
            Só conversas de verdade. Compare o custo por resposta, não o total:
            o total depende de quantas conversas cada um atendeu.
          </p>
        </Card>
      </div>
    </section>
  );
}

/**
 * O que alguém marcou como resposta ruim, esperando revisão.
 *
 * Fica na Análise porque é aqui que se olha o que o atendimento entregou, e
 * porque a revisão é semanal: não é coisa para resolver no meio do dia.
 *
 * Mostra a pergunta do lead junto da resposta — julgar uma resposta sem a
 * pergunta é impossível, e abrir dez conversas para revisar dez marcações
 * faria ninguém revisar.
 */
function RespostasRuins() {
  const [itens, setItens] = useState<RespostaRuim[] | null>(null);

  const carregar = () =>
    get<RespostaRuim[]>("/feedback?dias=90")
      .then(setItens)
      .catch(() => setItens([]));

  useEffect(() => {
    carregar();
  }, []);

  if (!itens || itens.length === 0) return null;

  async function tratar(ids: string[]) {
    await post("/feedback/tratar", { ids });
    carregar();
  }

  return (
    <section className="space-y-3">
      <div className="flex items-end justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold text-ink">
            Respostas marcadas como ruins
          </h2>
          <p className="text-xs text-ink-3 mt-0.5 max-w-prose">
            O material da revisão. Ajuste o prompt ou a base e marque como
            tratado — nada aqui muda nada sozinho.
          </p>
        </div>
        <Button
          className="h-8"
          icone={CheckCheck}
          onClick={() => tratar(itens!.map((i) => i.id))}
        >
          Tratar todas
        </Button>
      </div>

      <div className="space-y-2">
        {itens.map((r) => (
          <Card key={r.id} className="p-4" stripe="danger">
            <div className="flex flex-wrap items-center gap-2 text-xs text-ink-3">
              <span className="text-ink font-medium">
                {r.lead_name ?? "Lead sem nome"}
              </span>
              <span>·</span>
              <span>{ago(r.created_at)}</span>
              {r.por && (
                <>
                  <span>·</span>
                  <span>marcada por {r.por}</span>
                </>
              )}
              {r.modelo && (
                <span className="ml-auto">
                  {ROTULO_MODELO[r.modelo] ?? r.modelo}
                </span>
              )}
            </div>

            {r.antes && (
              <p className="text-sm text-ink-2 mt-3">
                <span className="text-ink-3">o lead disse: </span>
                {r.antes}
              </p>
            )}
            {r.resposta && (
              <p className="text-sm text-ink mt-2 px-3 py-2 rounded-lg bg-surface-2 border border-rule whitespace-pre-wrap">
                {r.resposta}
              </p>
            )}
            {r.comentario && (
              <p className="text-sm text-danger mt-2.5">
                <span className="text-ink-3">deveria ter: </span>
                {r.comentario}
              </p>
            )}

            <div className="mt-3">
              <Button className="h-8" icone={Check} onClick={() => tratar([r.id])}>
                Tratado
              </Button>
            </div>
          </Card>
        ))}
      </div>
    </section>
  );
}
